import React, { useMemo } from "react";

import { RouteComponentProps } from "react-router";

import useViewer from "../hooks/useViewer";

import useTranslations from "../hooks/useTranslations";
import useGetMads from "../graphql/hooks/queries/useGetMads";

import { getMadStatus } from "./MadListScreen";

import FollowUpCard from "../components/mads/FollowUpCard";
import {
  ScreenHeader,
  ScreenContainer,
  Container,
  H3,
  Link,
  Badge,
  EmptyState,
  H6,
} from "../components/wombat-ui";

import { CampaignIcon, PlusIcon } from "../components/icons";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import { MadFragment } from "../graphql/fragments";
import { NumberParam, useQueryParams } from "use-query-params";
import { StatisticsGrid } from "../components/wombat-ui/components/StatisticsGrid";

type CampaignScreenProps = RouteComponentProps<{ id: string }>;

const CampaignScreen: React.FC<CampaignScreenProps> = ({ match }) => {
  const t = useTranslations();
  const {
    isCapableOfReadingCampaigns,
    isCapableOfInsertingMads,
    id: viewerId,
    company: { campaigns },
  } = useViewer();

  const [{ page }] = useQueryParams({
    page: NumberParam,
  });

  // Match both id or slug
  const campaign = useMemo(
    () =>
      campaigns.find(({ id, slug }) => [id, slug].includes(match.params.id)),
    []
  );

  const { data: madData, loading: madLoading } = useGetMads({
    variables: {
      page: page || 1,
      per: 10,
      campaignId: campaign.id,
      sortBy: "createdAt",
      direction: "DESC"
    },
  });

  const mads = useMemo(() => madData?.mads.collection || [], [
    madData,
    madLoading,
  ]);

  const actions = useMemo(
    () => [
      campaign.canInsert && (
        <Link
          variant="button-subtle"
          key="hostEditButton"
          to={`/campaigns/${match.params.id}/edit`}
        >
          {t("campaign.edit")}
        </Link>
      ),
      isCapableOfInsertingMads && (
        <Link
          variant="button"
          key="campaignCreateMad"
          to={`/mads/new?campaignId=${campaign.id}`}
        >
          {t("header.createMad")}
          <PlusIcon iconClassName="w-4 h-4 ml-1 mt-0.5" />
        </Link>
      ),
    ],
    []
  );

  return (
    <ScreenContainer
      error={
        !isCapableOfReadingCampaigns ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        title={
          <span className="flex items-center space-x-4">
            {campaign.name}&nbsp;
            <Badge>
              {t(`campaign.madsCount`, { madsCount: campaign.madsCount })}
            </Badge>
          </span>
        }
        actions={actions}
        description={campaign.description || t("campaign.missingDescription")}
        icon={CampaignIcon}
      />
      <Container>
        <div className="flex flex-col py-5">
          <div>
            <H3>{t("campaign.analytics")}</H3>
            <StatisticsGrid
              data={[
                {
                  label: t("campaign.reportsCount") as string,
                  value: campaign.reportsCount,
                },
                {
                  label: t("campaign.uncheckedReportsCount") as string,
                  value: campaign.uncheckedReportsCount,
                },
                {
                  label: t("campaign.unfilledReportsCount") as string,
                  value: campaign.unfilledReportsCount,
                },
              ]}
            />
          </div>
          <H3>{t("campaign.followUps")}</H3>
          <List
            data={mads}
            listOnly
            loading={madLoading}
            id={`campaign-${campaign.id}-madList`}
            renderEmptyState={() => {
              return <H6>{t("campaign.noMads")}</H6>;
            }}
            renderAsList={(mad: MadFragment) => (
              <FollowUpCard
                key={mad.id}
                mad={mad}
                madType={getMadStatus(mad)}
                isTalentViewing={viewerId === mad.talent.id}
              />
            )}
          />
          {!madLoading && (
            <Pagination
              itemsCount={madData.mads.metadata.totalCount}
              itemsPerPage={10}
            />
          )}
        </div>
      </Container>
    </ScreenContainer>
  );
};

export default React.memo(CampaignScreen);
